import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { AddItemModal } from '../components/AddItemModal';
import { Plus, Search, Edit2, Trash2, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HTTP_UNAUTHORIZED = 401;

const CATEGORIES = [
  "All", "Dairy", "Cheese", "Meat", "Fish", "Oysters",
  "Fruits", "Vegetables", "Spices", "Spirits", "Dry Stock"
];

// --- Sub-components ---

const StatusBadge = ({ item }) => {
  if (item.is_low_stock) {
    return <span className="badge-low-stock">Low Stock</span>;
  }
  if (item.is_expiring_soon) {
    return <span className="badge-expiring">Expiring Soon</span>;
  }
  return <span className="badge-in-stock">In Stock</span>;
};

const FilterBar = ({ searchTerm, setSearchTerm, categoryFilter, setCategoryFilter, sortBy, setSortBy, sortOrder, setSortOrder }) => (
  <div className="card mb-6" data-testid="filters-section">
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#475569]" />
        <Input
          placeholder="Search items..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 border-[#E2E8F0]"
          data-testid="search-input"
        />
      </div>
      <Select value={categoryFilter} onValueChange={setCategoryFilter}>
        <SelectTrigger className="border-[#E2E8F0]" data-testid="category-filter">
          <Filter className="w-4 h-4 mr-2" />
          <SelectValue placeholder="Category" />
        </SelectTrigger>
        <SelectContent>
          {CATEGORIES.map((cat) => (
            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={sortBy} onValueChange={setSortBy}>
        <SelectTrigger className="border-[#E2E8F0]" data-testid="sort-by">
          <SelectValue placeholder="Sort by" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="name">Name</SelectItem>
          <SelectItem value="category">Category</SelectItem>
          <SelectItem value="quantity">Quantity</SelectItem>
          <SelectItem value="expiry_date">Expiry Date</SelectItem>
        </SelectContent>
      </Select>
      <Select value={sortOrder} onValueChange={setSortOrder}>
        <SelectTrigger className="border-[#E2E8F0]" data-testid="sort-order">
          <SelectValue placeholder="Order" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="asc">Ascending</SelectItem>
          <SelectItem value="desc">Descending</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

const formatExpiryDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString();
};

const InventoryTableRow = ({ item, onEdit, onDelete }) => (
  <tr className="table-row" data-testid={`item-row-${item.item_id}`}>
    <td className="table-cell font-semibold">{item.name}</td>
    <td className="table-cell">{item.category}</td>
    <td className="table-cell">{item.quantity} {item.unit}</td>
    <td className="table-cell">{formatExpiryDate(item.expiry_date)}</td>
    <td className="table-cell"><StatusBadge item={item} /></td>
    <td className="table-cell text-[#475569] text-xs">
      {item.updated_by && <span className="block">by {item.updated_by}</span>}
      {new Date(item.updated_at).toLocaleDateString()}
    </td>
    <td className="table-cell">
      <div className="flex gap-2">
        <button onClick={() => onEdit(item)} className="p-2 hover:bg-slate-100 transition-colors relative z-10" data-testid={`edit-btn-${item.item_id}`}>
          <Edit2 className="w-4 h-4 text-[#0A192F]" />
        </button>
        <button onClick={() => onDelete(item)} className="p-2 hover:bg-red-50 transition-colors relative z-10" data-testid={`delete-btn-${item.item_id}`}>
          <Trash2 className="w-4 h-4 text-red-600" />
        </button>
      </div>
    </td>
  </tr>
);

const InventoryTableBody = ({ loading, items, onEdit, onDelete, onAdd }) => {
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A192F] mx-auto"></div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="p-12 text-center">
        <p className="text-[#475569] font-body mb-4">No items found</p>
        <Button onClick={onAdd} variant="outline" className="border-[#0A192F] text-[#0A192F]">
          Add your first item
        </Button>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr>
            <th className="table-header">Item Name</th>
            <th className="table-header">Category</th>
            <th className="table-header">Quantity</th>
            <th className="table-header">Expiry Date</th>
            <th className="table-header">Status</th>
            <th className="table-header">Last Updated</th>
            <th className="table-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <InventoryTableRow key={item.item_id} item={item} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

// --- Main component ---

export const Inventory = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const fetchItems = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter && categoryFilter !== 'All') params.append('category', categoryFilter);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);

      const response = await axios.get(`${API_URL}/api/inventory?${params}`, {
        withCredentials: true
      });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      if (error.response?.status === HTTP_UNAUTHORIZED) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [searchTerm, categoryFilter, sortBy, sortOrder, navigate]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchItems();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchItems]);

  const handleAddItem = async (itemData) => {
    try {
      await axios.post(`${API_URL}/api/inventory`, itemData, { withCredentials: true });
      toast.success('Item added successfully');
      fetchItems();
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to add item');
    }
  };

  const handleEditItem = async (itemData) => {
    try {
      await axios.put(`${API_URL}/api/inventory/${editItem.item_id}`, itemData, {
        withCredentials: true
      });
      toast.success('Item updated successfully');
      setEditItem(null);
      fetchItems();
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  const handleDeleteItem = async () => {
    try {
      await axios.delete(`${API_URL}/api/inventory/${deleteItem.item_id}`, {
        withCredentials: true
      });
      toast.success('Item deleted successfully');
      setDeleteItem(null);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      <Sidebar />
      
      <main className="flex-1 p-8" data-testid="inventory-main">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#0A192F]">Inventory</h1>
            <p className="text-[#475569] font-body mt-1">Manage your kitchen inventory</p>
          </div>
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-[#0A192F] text-white hover:bg-[#0F294D] uppercase tracking-wide text-sm font-bold"
            data-testid="add-item-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Item
          </Button>
        </div>

        <FilterBar
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          categoryFilter={categoryFilter} setCategoryFilter={setCategoryFilter}
          sortBy={sortBy} setSortBy={setSortBy}
          sortOrder={sortOrder} setSortOrder={setSortOrder}
        />

        {/* Items Table */}
        <div className="card overflow-hidden p-0" data-testid="inventory-table">
          <InventoryTableBody
            loading={loading}
            items={items}
            onEdit={setEditItem}
            onDelete={setDeleteItem}
            onAdd={() => setShowAddModal(true)}
          />
        </div>
      </main>

      {/* Add/Edit Modal */}
      <AddItemModal
        open={showAddModal || !!editItem}
        onClose={() => {
          setShowAddModal(false);
          setEditItem(null);
        }}
        onSave={editItem ? handleEditItem : handleAddItem}
        item={editItem}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteItem} onOpenChange={(isOpen) => { if (!isOpen) setDeleteItem(null); }}>
        <AlertDialogContent className="bg-white" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl text-[#0A192F]">
              Delete Item
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#475569]">
              Are you sure you want to delete "{deleteItem?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E2E8F0]" data-testid="cancel-delete-btn">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItem}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
